import React, { useContext, useState } from 'react';
import i18next from 'core/i18n';
import {
  Phase3 as IPhase3,
  AgFinancingInput,
  AgFinancingPhase2Status,
  Phase2,
  Phase3Input,
  ContractStatus
} from '@indigo-ag/schema';
import { useMutation } from '@apollo/client';
import { ExecutionResult } from 'graphql';
import { Button, useFlashMessage } from '@indigo-ag/component-module';

import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import { PhaseWrapper } from '../shared/PhaseWrapper';
import { WarrantyType } from '../../WarrantyType/WarrantyType';
import { ContractState } from '../features/collateral-analysis/sections/ContractState/ContractState';
import { SignPhysicallySection } from '../features/collateral-analysis/sections/SignPhysicallySection/SignPhysicallySection';
import { UserContext } from 'core/components/GateWay/GateWay';
import { Phase3Util, utils } from 'shared';
import { MatriculaAnalysisSection } from '../features/collateral-analysis/sections/Analysis/MatriculaAnalisysSection';
import ConditionalsValidation from '../features/collateral-analysis/sections/ConditionalsValidation/ConditionalValidations';

interface IPhase3Context {
  updatePhase: (newPhase3Info: Partial<Phase3Input>) => Promise<ExecutionResult<any>>;
}

export const Phase3Context = React.createContext<IPhase3Context>({
  updatePhase: () => Promise.resolve({})
});

interface Phase3Props {
  phaseInfo?: IPhase3;
  matriculas: Phase2['matriculas'];
}

export const Phase3: React.FunctionComponent<Phase3Props> = ({ phaseInfo, matriculas }) => {
  /**
   * State
   */
  const [restarting, setRestarting] = useState(false);

  /**
   * Context's
   */
  const { agFinancingId, collateralType } = useContext(FinanceProcessContext);
  const { isInternal, isCreditLead } = useContext(UserContext);

  /**
   * Mutations
   */
  const [upsertFinancingAgFinancingFn] = useMutation(upsertFinancingAgFinancing);

  const updatePhase = (newPhase3Info: Partial<Phase3Input>) => {
    const newAgFinancingData: Partial<AgFinancingInput> = {
      id: agFinancingId,
      phase3: {
        ...newPhase3Info
      }
    };

    return upsertFinancingAgFinancingFn({
      variables: {
        data: newAgFinancingData
      }
    });
  };

  const { addFlashMessage } = useFlashMessage();

  const handleRestart = () => {
    setRestarting(true);

    upsertFinancingAgFinancingFn({
      variables: {
        data: {
          id: agFinancingId,
          phase2: {
            status: AgFinancingPhase2Status.PENDING_RESTART
          }
        }
      }
    })
      .then(() => {
        addFlashMessage({
          content: utils.text.firstLetterUppercase(i18next.t('phase2Restarted')),
          title: '',
          variant: 'success'
        });
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => setRestarting(false));
  };

  const showPhysicalSignSection =
    isInternal && !isCreditLead && phaseInfo?.contractStatus === ContractStatus.WAITING_PHYSICAL_SIGNATURE;

  return (
    <Phase3Context.Provider
      value={{
        updatePhase
      }}
    >
      <div className="flex">
        <PhaseWrapper
          title={i18next.t('collateralFormalization')}
          subtitleElement={<WarrantyType collateralType={collateralType} />}
          actionElement={
            phaseInfo && Phase3Util.canRestartPhase2(phaseInfo, isInternal) ? (
              <>
                <Button variant="secondary" size="small" onClick={() => handleRestart()} busy={restarting}>
                  {i18next.t('restartPhase2')}
                </Button>
              </>
            ) : (
              undefined
            )
          }
        >
          {phaseInfo && (
            <>
              <ConditionalsValidation phase3Info={phaseInfo} />
              <ContractState phase3Info={phaseInfo} />
              <MatriculaAnalysisSection analysisInfo={utils.getAnalysisData(matriculas)} includedInFormalization />
              {showPhysicalSignSection && <SignPhysicallySection phase3Info={phaseInfo} />}
            </>
          )}
        </PhaseWrapper>
      </div>
    </Phase3Context.Provider>
  );
};
